import { ENDPOINT, VEHICLES } from 'src/constants/api';
import store from 'src/redux/store';

import { baseApi, setHeaders } from '../base';

const unwrapApiData = <T>(response: unknown): T => {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    (response as { data?: unknown }).data !== undefined
  ) {
    return (response as { data: T }).data;
  }

  return response as T;
};

export type ImageProcessingStatus =
  | 'queued'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'expired'
  | 'discarded'
  | 'applied';

export type ImageProcessingMode = 'enhancement';

export type ImageProcessingJob = {
  id: number;
  feature_key: string;
  mode: ImageProcessingMode;
  media_id: number;
  status: ImageProcessingStatus;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  status_url?: string;
  result_url?: string;
  result_expires_at?: string;
  error?: {
    code: string;
    message: string;
  };
};

export type ImageProcessingUploadSignItem = {
  position: number;
  path: string;
  upload_url: string;
  headers: Record<string, string>;
};

export type ImageProcessingUploadSignResponse = {
  bucket: string;
  items: ImageProcessingUploadSignItem[];
};

export const parseUploadSignResponse = (
  response: unknown
): ImageProcessingUploadSignResponse | null => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  if ('items' in response && Array.isArray((response as ImageProcessingUploadSignResponse).items)) {
    return response as ImageProcessingUploadSignResponse;
  }

  if ('data' in response && (response as { data?: unknown }).data) {
    return parseUploadSignResponse((response as { data: unknown }).data);
  }

  return null;
};

type ImageProcessingParams = {
  vehicleId: number;
  mediaId: number;
};

type ImageProcessingJobParams = ImageProcessingParams & {
  jobId: number;
};

export const imageProcessingJobsPath = ({ vehicleId, mediaId }: ImageProcessingParams) =>
  `${ENDPOINT}/${VEHICLES}/${vehicleId}/media/${mediaId}/image-processing/jobs`;

export const fetchImageProcessingResult = async ({
  vehicleId,
  mediaId,
  jobId,
}: ImageProcessingJobParams): Promise<Blob> => {
  const headers = new Headers();
  setHeaders(headers, { getState: store.getState });

  const response = await fetch(`${imageProcessingJobsPath({ vehicleId, mediaId })}/${jobId}/result`, {
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    throw { status: response.status, data: errorBody };
  }

  return response.blob();
};

export const fetchImageProcessingUploadSign = async ({
  vehicleId,
  mediaId,
  jobId,
  mimeType,
}: ImageProcessingJobParams & { mimeType: string }): Promise<ImageProcessingUploadSignResponse> => {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });
  setHeaders(headers, { getState: store.getState });

  const response = await fetch(
    `${imageProcessingJobsPath({ vehicleId, mediaId })}/${jobId}/upload-sign`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ mime_type: mimeType }),
    }
  );

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    throw { status: response.status, data: body };
  }

  const parsed = parseUploadSignResponse(body);

  if (!parsed?.items?.[0]) {
    throw {
      status: 500,
      data: { message: 'Invalid upload-sign response', body },
    };
  }

  return parsed;
};

const SUPPORTED_PROCESSING_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const normalizeProcessingMimeType = (mimeType?: string) =>
  mimeType && SUPPORTED_PROCESSING_MIME_TYPES.includes(mimeType) ? mimeType : 'image/jpeg';

export const buildS3UploadHeaders = (
  uploadItem: ImageProcessingUploadSignItem
): Record<string, string> => {
  const uploadHeaders = { ...uploadItem.headers };

  if (
    !uploadHeaders['x-amz-acl'] &&
    uploadItem.upload_url.includes('x-amz-acl=public-read')
  ) {
    uploadHeaders['x-amz-acl'] = 'public-read';
  }

  return uploadHeaders;
};

const imageProcessingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createImageProcessingJob: builder.mutation<ImageProcessingJob, ImageProcessingParams>({
      query: ({ vehicleId, mediaId }) => ({
        url: `${imageProcessingJobsPath({ vehicleId, mediaId })}`,
        method: 'POST',
        body: { mode: 'enhancement' },
      }),
      transformResponse: unwrapApiData<ImageProcessingJob>,
    }),
    getImageProcessingJobStatus: builder.query<ImageProcessingJob, ImageProcessingJobParams>({
      query: ({ vehicleId, mediaId, jobId }) => ({
        url: `${imageProcessingJobsPath({ vehicleId, mediaId })}/${jobId}`,
        method: 'GET',
      }),
      transformResponse: unwrapApiData<ImageProcessingJob>,
    }),
    applyImageProcessingJob: builder.mutation<any, ImageProcessingJobParams>({
      query: ({ vehicleId, mediaId, jobId }) => ({
        url: `${imageProcessingJobsPath({ vehicleId, mediaId })}/${jobId}/apply`,
        method: 'POST',
      }),
      transformResponse: unwrapApiData<any>,
    }),
    discardImageProcessingJob: builder.mutation<
      { id: number; feature_key: string; mode: ImageProcessingMode; status: ImageProcessingStatus },
      ImageProcessingJobParams
    >({
      query: ({ vehicleId, mediaId, jobId }) => ({
        url: `${imageProcessingJobsPath({ vehicleId, mediaId })}/${jobId}/discard`,
        method: 'POST',
      }),
      transformResponse: unwrapApiData<{
        id: number;
        feature_key: string;
        mode: ImageProcessingMode;
        status: ImageProcessingStatus;
      }>,
    }),
  }),
});

export const {
  useCreateImageProcessingJobMutation,
  useLazyGetImageProcessingJobStatusQuery,
  useApplyImageProcessingJobMutation,
  useDiscardImageProcessingJobMutation,
} = imageProcessingApi;

export const isActiveImageProcessingStatus = (status?: ImageProcessingStatus) =>
  status === 'queued' || status === 'processing';

export const isTerminalImageProcessingStatus = (status?: ImageProcessingStatus) =>
  status === 'succeeded' ||
  status === 'failed' ||
  status === 'expired' ||
  status === 'discarded' ||
  status === 'applied';

export const isActiveProcessingJobConflict = (error: any) =>
  error?.status === 409 &&
  error?.data?.error === 'PROCESSING_ALREADY_ACTIVE' &&
  error?.data?.data?.id;
